/**
 * Credits role claims (ADR 6 / C-3.1, gated in C-3.2).
 *
 * Read only the real NextAuth session's `user.groups`. Do not infer from
 * `session.user.isAdmin`, org `ADMIN_ROLE`, or a View-as target (M4S-5176).
 * Hiding a tab or edit control is presentation only — data denial is C-3.4.
 */

import { UserRole } from '@/@portal/types/constants';

export const CREDITS_RATE_ADMIN_CLAIM = 'credits.rate.admin';
export const CREDITS_MARGIN_READ_CLAIM = 'credits.margin.read';

export type CreditClaimFlags = {
    rateAdmin: boolean;
    marginRead: boolean;
};

/** Withhold a claim in local/dev without editing the fixture: CREDITS_WITHHOLD_RATE_ADMIN=1 or CREDITS_WITHHOLD_MARGIN_READ=1. */
export function creditClaimsFromEnv(env: NodeJS.ProcessEnv = process.env): CreditClaimFlags {
    return {
        rateAdmin: env.CREDITS_WITHHOLD_RATE_ADMIN !== '1',
        marginRead: env.CREDITS_WITHHOLD_MARGIN_READ !== '1',
    };
}

export function withCreditClaims(
    groups: string[],
    flags: CreditClaimFlags = creditClaimsFromEnv(),
): string[] {
    const next = groups.filter(
        (group) => group !== CREDITS_RATE_ADMIN_CLAIM && group !== CREDITS_MARGIN_READ_CLAIM,
    );
    if (flags.rateAdmin) next.push(CREDITS_RATE_ADMIN_CLAIM);
    if (flags.marginRead) next.push(CREDITS_MARGIN_READ_CLAIM);
    return next;
}

export function sessionHasCreditClaim(
    session: { user?: { groups?: string[] } } | null | undefined,
    claim: string,
): boolean {
    return Boolean(session?.user?.groups?.includes(claim));
}

/**
 * Cognito groups that pass the API's `@Auth({ roles: [UserRole.ADMIN] })`: `admin`, and
 * `developer`, which inherits it (`ROLE_HIERARCHY` in mfour-studio-api). Cognito groups
 * are lowercase (`UserRole` values); the e2e session fixture spells them in capitals.
 */
export const CREDITS_ADMIN_GROUPS: readonly string[] = [UserRole.ADMIN, UserRole.DEVELOPER];

/**
 * Whether the real session passes the API's ADMIN check (M4S-5911 phase A: revoke and
 * the contract editor). Mirrors `RolesGuard`: the Cognito groups plus `custom:role`.
 * Presentation only: the API refuses an INTERNAL-only caller with 403. Never reads
 * `isAdmin` or a View-as org. Replaced by the server's
 * `GET admin/credit-consumption/permissions` when the CS page lands (§5).
 */
export function sessionIsCreditsAdmin(
    session: { user?: { groups?: string[]; role?: string } } | null | undefined,
): boolean {
    const user = session?.user;
    if (!user) return false;
    const held = [...(user.groups ?? []), user.role ?? ''].map((group) => group.toLowerCase());
    return held.some((group) => CREDITS_ADMIN_GROUPS.includes(group));
}
